/**
 * Canto API client.
 *
 * A thin, typed wrapper around `fetch` that:
 *   1. Acquires an Entra access token from MSAL on every request (silently
 *      if possible, falling back to redirect for expired sessions).
 *   2. Attaches `Authorization: Bearer <token>` and `Content-Type: application/json`.
 *   3. Parses the standard error envelope `{ error: { code, message, field? } }`
 *      and throws a `CantoApiError` so callers can handle API-level failures
 *      distinctly from network errors.
 *   4. Exposes per-resource helpers (getMe, listTaps, …) that return typed
 *      domain objects from `./types`.
 *
 * Base URL: relative `/api/v1` — in development the Vite proxy forwards
 * these to the Azure Functions host on port 7071.
 */

import type { AccountInfo } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes, msalInstance } from '../lib/msal';
import type {
  Beer,
  BeerLocationPrice,
  BotChannelMapping,
  EntraUser,
  EntraUserCreateResponse,
  CantoEvent,
  EventImpact,
  EventType,
  KegSize,
  KickResult,
  ListEnvelope,
  Location,
  MenuCurrentData,
  MenuPublication,
  MeResponse,
  Reservation,
  ReservationAuditLogEntry,
  ReservationStatus,
  Supply,
  SupplyPriority,
  AdminJobsState,
  SportsEventImportResult,
  TicketmasterEventImportResult,
  SyncState,
  Tap,
  TapAssignment,
  TapChangeLogEntry,
  Task,
  TaskRoleScope,
  TimeslipDetail,
  TimeslipSummary,
  User,
  TeamsTimecard,
  TeamsShift,
  TeamsSyncSummary,
} from './types';

export const API_BASE = `${import.meta.env.VITE_CANTO_API_BASE_URL ?? ''}/api/v1`;

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export class CantoApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: string | undefined;

  constructor(status: number, code: string, message: string, field?: string) {
    super(message);
    this.name = 'CantoApiError';
    this.status = status;
    this.code = code;
    this.field = field;
  }

  /** True if this is an auth failure (should trigger sign-in). */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True if the user lacks the required role. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True if the requested resource doesn't exist. */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

// ---------------------------------------------------------------------------
// Token acquisition
// ---------------------------------------------------------------------------

export async function acquireToken(): Promise<string> {
  // Try to find an active account. MsalProvider keeps the active account
  // in sync, but we also check all accounts as a fallback.
  let account: AccountInfo | null = msalInstance.getActiveAccount();
  if (!account) {
    const all = msalInstance.getAllAccounts();
    account = all[0] ?? null;
  }
  if (!account) {
    // No account means the user isn't signed in. Trigger interactive login.
    await msalInstance.loginRedirect({ scopes: apiScopes });
    // loginRedirect never resolves — execution stops here while the browser
    // navigates. Throw to satisfy the type system.
    throw new CantoApiError(401, 'UNAUTHORIZED', 'Redirecting to sign-in');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: apiScopes,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Token expired or consent required — redirect interactively.
      await msalInstance.acquireTokenRedirect({ account, scopes: apiScopes });
      throw new CantoApiError(401, 'UNAUTHORIZED', 'Redirecting to sign-in');
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Core request helper
// ---------------------------------------------------------------------------

async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await acquireToken();
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const response = await fetch(url, init);

  if (!response.ok) {
    // Try to parse the error envelope; fall back to a generic message.
    let code = 'INTERNAL_ERROR';
    let message = `HTTP ${response.status}`;
    let field: string | undefined;
    try {
      const envelope = (await response.json()) as {
        error?: { code?: string; message?: string; field?: string };
      };
      if (envelope.error) {
        code = envelope.error.code ?? code;
        message = envelope.error.message ?? message;
        field = envelope.error.field;
      }
    } catch {
      // JSON parse failed — use the status text as the message.
      message = response.statusText || message;
    }
    throw new CantoApiError(response.status, code, message, field);
  }

  // 204 No Content — return undefined cast to T (callers that use 204 return void).
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Per-resource helpers
// ---------------------------------------------------------------------------

// --- /me -------------------------------------------------------------------

export function getMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>('GET', '/me');
}

// --- /locations ------------------------------------------------------------

export function listLocations(params?: {
  includeDeleted?: boolean;
}): Promise<ListEnvelope<Location>> {
  const qs = params?.includeDeleted ? '?includeDeleted=true' : '';
  return apiRequest<ListEnvelope<Location>>('GET', `/locations${qs}`);
}

export function getLocation(locationId: string): Promise<Location> {
  return apiRequest<Location>('GET', `/locations/${locationId}`);
}

// --- /users ----------------------------------------------------------------

export function listUsers(params?: {
  search?: string;
  isActive?: boolean;
  offset?: number;
  limit?: number;
}): Promise<ListEnvelope<User>> {
  const qs = buildQs(params ?? {});
  return apiRequest<ListEnvelope<User>>('GET', `/users${qs}`);
}

// --- /beers ----------------------------------------------------------------

export function listBeers(params?: {
  status?: string;
  tags?: string[];
  search?: string;
  offset?: number;
  limit?: number;
}): Promise<ListEnvelope<Beer>> {
  const { tags = [], ...rest } = params ?? {};
  const base = buildQs(rest);
  const tagStr = tags.map((t) => `tags=${encodeURIComponent(t)}`).join('&');
  const qs = [base.slice(1), tagStr].filter(Boolean).join('&');
  return apiRequest<ListEnvelope<Beer>>('GET', `/beers${qs ? `?${qs}` : ''}`);
}

export function getBeer(beerId: string): Promise<Beer> {
  return apiRequest<Beer>('GET', `/beers/${beerId}`);
}

export function getBeerPriceForLocation(
  beerId: string,
  locationId: string,
): Promise<BeerLocationPrice> {
  return apiRequest<BeerLocationPrice>('GET', `/beers/${beerId}/prices/${locationId}`);
}

export function listBeerPrices(beerId: string): Promise<{ items: BeerLocationPrice[] }> {
  return apiRequest<{ items: BeerLocationPrice[] }>('GET', `/beers/${beerId}/prices`);
}

export interface CreateBeerBody {
  name: string;
  style: string;
  menuDescription?: string | null;
  description?: string | null;
  abv?: number | null;
  priceKeg?: number | null;
  priceSixtel?: number | null;
  status?: 'active' | 'pending';
  tags?: string[];
}

export interface PatchBeerBody {
  name?: string;
  style?: string;
  menuDescription?: string | null;
  description?: string | null;
  abv?: number | null;
  priceKeg?: number | null;
  priceSixtel?: number | null;
  status?: 'active' | 'retired' | 'pending';
  tags?: string[];
}

export interface UpsertBeerPriceBody {
  pricePint?: number | null;
  price10oz?: number | null;
  priceGrowler?: number | null;
  priceHowler?: number | null;
  priceCrowler?: number | null;
}

export interface GenerateBeerDescriptionsBody {
  beerName: string;
  beerStyle: string;
  brewerNotes: {
    brewProcess?: string | null;
    ingredients?: string | null;
    namingStory?: string | null;
    tastingNotes?: string | null;
    additionalContext?: string | null;
  };
}

export interface GenerateBeerDescriptionsResponse {
  menuDescription: string;
  description: string;
}

export function createBeer(body: CreateBeerBody): Promise<Beer> {
  return apiRequest<Beer>('POST', '/beers', body);
}

export function patchBeer(beerId: string, body: PatchBeerBody): Promise<Beer> {
  return apiRequest<Beer>('PATCH', `/beers/${beerId}`, body);
}

export function deleteBeer(beerId: string): Promise<Beer> {
  return apiRequest<Beer>('DELETE', `/beers/${beerId}`);
}

export async function uploadBeerArtwork(
  beerId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<Beer> {
  const token = await acquireToken();
  const form = new FormData();
  form.append('file', file);

  return new Promise<Beer>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/beers/${beerId}/artwork`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as Beer);
      } else {
        try {
          const env = JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string } };
          reject(new CantoApiError(xhr.status, env.error?.code ?? 'INTERNAL_ERROR', env.error?.message ?? `HTTP ${xhr.status}`));
        } catch {
          reject(new CantoApiError(xhr.status, 'INTERNAL_ERROR', `HTTP ${xhr.status}`));
        }
      }
    };
    xhr.onerror = () => reject(new CantoApiError(0, 'INTERNAL_ERROR', 'Network error'));
    xhr.send(form);
  });
}

export function upsertBeerPrice(
  beerId: string,
  locationId: string,
  body: UpsertBeerPriceBody,
): Promise<BeerLocationPrice> {
  return apiRequest<BeerLocationPrice>('PUT', `/beers/${beerId}/prices/${locationId}`, body);
}

export function deleteBeerPrice(beerId: string, locationId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/beers/${beerId}/prices/${locationId}`);
}

export function suggestStyles(q: string): Promise<{ suggestions: { style: string; count: number }[] }> {
  return apiRequest('GET', `/beers/styles/suggest?q=${encodeURIComponent(q)}&limit=10`);
}

export function suggestTags(q: string): Promise<{ suggestions: { tag: string; count: number }[] }> {
  return apiRequest('GET', `/beers/tags/suggest?q=${encodeURIComponent(q)}&limit=10`);
}

export function generateBeerDescriptions(
  body: GenerateBeerDescriptionsBody,
): Promise<GenerateBeerDescriptionsResponse> {
  return apiRequest<GenerateBeerDescriptionsResponse>('POST', '/beers/description/generate', body);
}

// --- /taps -----------------------------------------------------------------

export function listTapsAtLocation(
  locationId: string,
  params?: { includeKicked?: boolean },
): Promise<ListEnvelope<Tap>> {
  const qs = params?.includeKicked ? '?includeKicked=true' : '';
  return apiRequest<ListEnvelope<Tap>>('GET', `/locations/${locationId}/taps${qs}`);
}

export function getTap(tapId: string): Promise<Tap> {
  return apiRequest<Tap>('GET', `/taps/${tapId}`);
}

export function patchTap(
  tapId: string,
  body: { label?: string | null; isPublic?: boolean },
): Promise<Tap> {
  return apiRequest<Tap>('PATCH', `/taps/${tapId}`, body);
}

// --- /tap-assignments ------------------------------------------------------

export function listTapAssignmentsForTap(
  tapId: string,
  params?: { status?: string[]; offset?: number; limit?: number },
): Promise<ListEnvelope<TapAssignment>> {
  const { status = [], ...rest } = params ?? {};
  const base = buildQs(rest);
  const statusStr = status.map((s) => `status=${encodeURIComponent(s)}`).join('&');
  const qs = [base.slice(1), statusStr].filter(Boolean).join('&');
  return apiRequest<ListEnvelope<TapAssignment>>(
    'GET',
    `/taps/${tapId}/assignments${qs ? `?${qs}` : ''}`,
  );
}

// Exported body types so mutation hooks and form components can share them.
export interface CreateAssignmentBody {
  beerId: string;
  status: 'on' | 'reserved';
  tappedAt?: string;
  kegSize?: KegSize | null;
  notes?: string | null;
}

export interface PatchAssignmentBody {
  beerId?: string;
  kegSize?: KegSize | null;
  notes?: string | null;
}

export function createTapAssignment(
  tapId: string,
  body: CreateAssignmentBody,
): Promise<TapAssignment> {
  return apiRequest<TapAssignment>('POST', `/taps/${tapId}/assignments`, body);
}

export function patchTapAssignment(
  assignmentId: string,
  body: PatchAssignmentBody,
): Promise<TapAssignment> {
  return apiRequest<TapAssignment>('PATCH', `/tap-assignments/${assignmentId}`, body);
}

export function kickTapAssignment(
  assignmentId: string,
  promoteReserved = false,
): Promise<KickResult> {
  const qs = promoteReserved ? '?promoteReserved=true' : '';
  return apiRequest<KickResult>('POST', `/tap-assignments/${assignmentId}/kick${qs}`);
}

export function promoteTapAssignment(assignmentId: string): Promise<TapAssignment> {
  return apiRequest<TapAssignment>('POST', `/tap-assignments/${assignmentId}/promote`);
}

export function listTapAssignments(params: {
  locationId: string;
  status?: string[];
  offset?: number;
  limit?: number;
}): Promise<ListEnvelope<TapAssignment>> {
  const { locationId, status = [], ...rest } = params;
  const base = buildQs(rest);
  const statusStr = status.map((s) => `status=${encodeURIComponent(s)}`).join('&');
  const qs = [base.slice(1), statusStr].filter(Boolean).join('&');
  return apiRequest<ListEnvelope<TapAssignment>>(
    'GET',
    `/locations/${locationId}/tap-assignments${qs ? `?${qs}` : ''}`,
  );
}

// --- /supplies -------------------------------------------------------------

export function listSupplies(
  locationId: string,
  params?: {
    status?: string[];
    priority?: string[];
    search?: string;
    includeDeleted?: boolean;
    sort?: string;
    offset?: number;
    limit?: number;
  },
): Promise<ListEnvelope<Supply>> {
  const { status = [], priority = [], ...rest } = params ?? {};
  const base = buildQs(rest);
  const statusStr   = status.map((s) => `status=${encodeURIComponent(s)}`).join('&');
  const priorityStr = priority.map((p) => `priority=${encodeURIComponent(p)}`).join('&');
  const qs = [base.slice(1), statusStr, priorityStr].filter(Boolean).join('&');
  return apiRequest<ListEnvelope<Supply>>(
    'GET',
    `/locations/${locationId}/supplies${qs ? `?${qs}` : ''}`,
  );
}

export interface CreateSupplyBody {
  name: string;
  quantity?: number | null;
  notes?: string | null;
  priority?: SupplyPriority;
}

export interface PatchSupplyBody {
  name?: string;
  quantity?: number | null;
  notes?: string | null;
  priority?: SupplyPriority;
}

export function createSupply(locationId: string, body: CreateSupplyBody): Promise<Supply> {
  return apiRequest<Supply>('POST', `/locations/${locationId}/supplies`, body);
}

export function getSupply(supplyId: string): Promise<Supply> {
  return apiRequest<Supply>('GET', `/supplies/${supplyId}`);
}

export function patchSupply(supplyId: string, body: PatchSupplyBody): Promise<Supply> {
  return apiRequest<Supply>('PATCH', `/supplies/${supplyId}`, body);
}

export function deleteSupply(supplyId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/supplies/${supplyId}`);
}

export function acknowledgeSupply(supplyId: string): Promise<Supply> {
  return apiRequest<Supply>('POST', `/supplies/${supplyId}/acknowledge`);
}

export function orderSupply(
  supplyId: string,
  body?: { notes?: string | null },
): Promise<Supply> {
  return apiRequest<Supply>('POST', `/supplies/${supplyId}/order`, body);
}

export function receiveSupply(supplyId: string): Promise<Supply> {
  return apiRequest<Supply>('POST', `/supplies/${supplyId}/receive`);
}

export function closeSupply(supplyId: string): Promise<Supply> {
  return apiRequest<Supply>('POST', `/supplies/${supplyId}/close`);
}

export function cancelSupply(
  supplyId: string,
  body?: { reason?: string | null },
): Promise<Supply> {
  return apiRequest<Supply>('POST', `/supplies/${supplyId}/cancel`, body);
}

// --- /tasks ----------------------------------------------------------------

export function listTasks(
  locationId: string,
  params?: {
    status?: 'open' | 'completed' | 'all';
    assignedRoleScope?: string;
    offset?: number;
    limit?: number;
  },
): Promise<ListEnvelope<Task>> {
  const qs = buildQs(params ?? {});
  return apiRequest<ListEnvelope<Task>>('GET', `/locations/${locationId}/tasks${qs}`);
}

export function completeTask(taskId: string): Promise<Task> {
  return apiRequest<Task>('POST', `/tasks/${taskId}/complete`);
}

export function uncompleteTask(taskId: string): Promise<Task> {
  return apiRequest<Task>('POST', `/tasks/${taskId}/uncomplete`);
}

export interface CreateTaskBody {
  name: string;
  notes?: string | null;
  dueAt?: string | null;
  assignedRoleScope?: TaskRoleScope;
}

export interface PatchTaskBody {
  name?: string;
  notes?: string | null;
  dueAt?: string | null;
  assignedRoleScope?: TaskRoleScope;
}

export function createTask(locationId: string, body: CreateTaskBody): Promise<Task> {
  return apiRequest<Task>('POST', `/locations/${locationId}/tasks`, body);
}

export function getTask(taskId: string): Promise<Task> {
  return apiRequest<Task>('GET', `/tasks/${taskId}`);
}

export function patchTask(taskId: string, body: PatchTaskBody): Promise<Task> {
  return apiRequest<Task>('PATCH', `/tasks/${taskId}`, body);
}

export function deleteTask(taskId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/tasks/${taskId}`);
}

// --- /events ---------------------------------------------------------------

export function listEvents(params?: {
  from?: string;
  to?: string;
  locationId?: string[];
  type?: string[];
  offset?: number;
  limit?: number;
}): Promise<ListEnvelope<CantoEvent>> {
  const { locationId = [], type = [], ...rest } = params ?? {};
  const base = buildQs(rest);
  const locStr = locationId.map((l) => `locationId=${encodeURIComponent(l)}`).join('&');
  const typeStr = type.map((t) => `type=${encodeURIComponent(t)}`).join('&');
  const qs = [base.slice(1), locStr, typeStr].filter(Boolean).join('&');
  return apiRequest<ListEnvelope<CantoEvent>>('GET', `/events${qs ? `?${qs}` : ''}`);
}

export function upcomingEvents(
  locationId: string,
  days?: number,
): Promise<ListEnvelope<CantoEvent>> {
  const qs = days !== undefined ? `?days=${days}` : '';
  return apiRequest<ListEnvelope<CantoEvent>>(
    'GET',
    `/locations/${locationId}/events/upcoming${qs}`,
  );
}

export interface CreateEventBody {
  name: string;
  type: EventType;
  startAt: string;
  endAt: string;
  expectedImpact?: EventImpact | null;
  notes?: string | null;
  locationIds?: string[];
}

export interface PatchEventBody {
  name?: string;
  type?: EventType;
  startAt?: string;
  endAt?: string;
  expectedImpact?: EventImpact | null;
  notes?: string | null;
  locationIds?: string[];
}

export function createEvent(body: CreateEventBody): Promise<CantoEvent> {
  return apiRequest<CantoEvent>('POST', '/events', body);
}

export function patchEvent(eventId: string, body: PatchEventBody): Promise<CantoEvent> {
  return apiRequest<CantoEvent>('PATCH', `/events/${eventId}`, body);
}

export function deleteEvent(eventId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/events/${eventId}`);
}

// --- /timeslips ------------------------------------------------------------

export function listTimeslips(
  locationId: string,
  params?: {
    status?: string[];
    from?: string;
    to?: string;
    offset?: number;
    limit?: number;
  },
): Promise<ListEnvelope<TimeslipSummary>> {
  const { status = [], ...rest } = params ?? {};
  const base = buildQs(rest);
  const statusStr = status.map((s) => `status=${encodeURIComponent(s)}`).join('&');
  const qs = [base.slice(1), statusStr].filter(Boolean).join('&');
  return apiRequest<ListEnvelope<TimeslipSummary>>(
    'GET',
    `/locations/${locationId}/timeslips${qs ? `?${qs}` : ''}`,
  );
}

export function getTimeslip(timeslipId: string): Promise<TimeslipDetail> {
  return apiRequest<TimeslipDetail>('GET', `/timeslips/${timeslipId}`);
}

export function submitTimeslip(timeslipId: string): Promise<TimeslipDetail> {
  return apiRequest<TimeslipDetail>('POST', `/timeslips/${timeslipId}/submit`);
}

// --- /locations/{locationId}/menus -----------------------------------------

export function getCurrentMenu(locationId: string): Promise<MenuCurrentData> {
  return apiRequest<MenuCurrentData>('GET', `/locations/${locationId}/menus/current`);
}

export function listMenuPublications(
  locationId: string,
  params?: { offset?: number; limit?: number },
): Promise<ListEnvelope<MenuPublication>> {
  const qs = buildQs(params ?? {});
  return apiRequest<ListEnvelope<MenuPublication>>(
    'GET',
    `/locations/${locationId}/menus/publications${qs}`,
  );
}

export interface PublishMenuBody {
  formats: ('html' | 'json')[];
  includeTapIds?: string[];
  notifyWordPressAdmin?: boolean;
}

export function publishMenu(
  locationId: string,
  body: PublishMenuBody,
): Promise<MenuPublication> {
  return apiRequest<MenuPublication>('POST', `/locations/${locationId}/menus/publications`, body);
}

export function getMenuPublication(publicationId: string): Promise<MenuPublication> {
  return apiRequest<MenuPublication>('GET', `/menu-publications/${publicationId}`);
}

export async function downloadMenuFile(
  publicationId: string,
  format: 'html' | 'json',
  filename: string,
): Promise<void> {
  const token = await acquireToken();
  const url = `${API_BASE}/menu-publications/${publicationId}/file?format=${format}`;
  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) {
    throw new CantoApiError(resp.status, 'DOWNLOAD_ERROR', `HTTP ${resp.status}`);
  }
  const blob = await resp.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

// --- /ops/bot/channel-mappings ---------------------------------------------

export function listChannelMappings(): Promise<{ items: BotChannelMapping[] }> {
  return apiRequest<{ items: BotChannelMapping[] }>('GET', '/ops/bot/channel-mappings');
}

export interface UpsertChannelMappingBody {
  teamsTeamId: string;
  teamsChannelId: string;
  displayName?: string | null;
}

export function upsertChannelMapping(
  locationId: string,
  body: UpsertChannelMappingBody,
): Promise<BotChannelMapping> {
  return apiRequest<BotChannelMapping>('PUT', `/ops/bot/channel-mappings/${locationId}`, body);
}

export function deleteChannelMapping(locationId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/ops/bot/channel-mappings/${locationId}`);
}

// --- /sync -----------------------------------------------------------------

export function getSyncState(): Promise<SyncState> {
  return apiRequest<SyncState>('GET', '/sync/users/state');
}

export function getJobsState(): Promise<AdminJobsState> {
  return apiRequest<AdminJobsState>('GET', '/ops/jobs/state');
}

export function triggerSync(forceFull = false): Promise<SyncState> {
  return apiRequest<SyncState>('POST', '/sync/users/run', { forceFull });
}

export function triggerSportsEventImport(): Promise<SportsEventImportResult> {
  const run = (path: string) =>
    apiRequest<SportsEventImportResult>('POST', path);

  return run('/ops/sports-import/run').catch((err: unknown) => {
    if (!(err instanceof CantoApiError) || !err.isNotFound) {
      throw err;
    }
    return run('/ops/sports/import/run').catch((legacyErr: unknown) => {
      if (!(legacyErr instanceof CantoApiError) || !legacyErr.isNotFound) {
        throw legacyErr;
      }
      return run('/ops/jobs/sports/run');
    });
  });
}

export function triggerTicketmasterEventImport(): Promise<TicketmasterEventImportResult> {
  const run = (path: string) =>
    apiRequest<TicketmasterEventImportResult>('POST', path);

  return run('/ops/ticketmaster-import/run').catch((err: unknown) => {
    if (!(err instanceof CantoApiError) || !err.isNotFound) {
      throw err;
    }
    return run('/ops/jobs/ticketmaster/run');
  });
}

// --- /ops/entra-users ------------------------------------------------------

export function listEntraUsers(params?: { jobTitle?: string }): Promise<{ items: EntraUser[] }> {
  const qs = buildQs(params ?? {});
  return apiRequest<{ items: EntraUser[] }>('GET', `/ops/entra-users${qs}`);
}

export interface CreateEntraUserBody {
  givenName: string;
  surname: string;
  jobTitle: string;
  mobilePhone?: string | null;
  otherMails: string[];
}

export function createEntraUser(body: CreateEntraUserBody): Promise<EntraUserCreateResponse> {
  return apiRequest<EntraUserCreateResponse>('POST', '/ops/entra-users', body);
}

export interface CreateBartenderBody {
  givenName: string;
  surname: string;
  personalEmail: string;
  hireDate: string;
  mobilePhone?: string | null;
  groups?: string[];
}

export function createBartender(body: CreateBartenderBody): Promise<EntraUserCreateResponse> {
  return apiRequest<EntraUserCreateResponse>('POST', '/ops/entra-users/bartenders', body);
}

export interface UpdateEntraUserBody {
  givenName?: string | undefined;
  surname?: string | undefined;
  displayName?: string | undefined;
  mobilePhone?: string | null | undefined;
  otherMails?: string[] | undefined;
  hireDate?: string | null | undefined;
}

export function updateEntraUser(userId: string, body: UpdateEntraUserBody): Promise<EntraUser> {
  return apiRequest<EntraUser>('PATCH', `/ops/entra-users/${userId}`, body);
}

export function deleteEntraUser(userId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/ops/entra-users/${userId}`);
}

export function generateBartenderTap(userId: string): Promise<EntraUserCreateResponse> {
  return apiRequest<EntraUserCreateResponse>('POST', `/ops/entra-users/${userId}/tap`);
}

// --- /reservations ---------------------------------------------------------

export function listReservations(
  locationId: string,
  params?: {
    status?: ReservationStatus;
    from?: string;
    to?: string;
    offset?: number;
    limit?: number;
  },
): Promise<ListEnvelope<Reservation>> {
  const qs = buildQs(params ?? {});
  return apiRequest<ListEnvelope<Reservation>>(
    'GET',
    `/locations/${locationId}/reservations${qs}`,
  );
}

export interface CreateReservationBody {
  name: string;
  partySize: number;
  reservedAt: string;
  phone?: string | null;
  specialRequests?: string | null;
  status?: ReservationStatus;
}

export interface PatchReservationBody {
  name?: string;
  partySize?: number;
  reservedAt?: string;
  phone?: string | null;
  specialRequests?: string | null;
  status?: ReservationStatus;
}

export function createReservation(
  locationId: string,
  body: CreateReservationBody,
): Promise<Reservation> {
  return apiRequest<Reservation>('POST', `/locations/${locationId}/reservations`, body);
}

export function getReservation(reservationId: string): Promise<Reservation> {
  return apiRequest<Reservation>('GET', `/reservations/${reservationId}`);
}

export function patchReservation(
  reservationId: string,
  body: PatchReservationBody,
): Promise<Reservation> {
  return apiRequest<Reservation>('PATCH', `/reservations/${reservationId}`, body);
}

export function deleteReservation(reservationId: string): Promise<Reservation> {
  return apiRequest<Reservation>('DELETE', `/reservations/${reservationId}`);
}

export function getReservationAuditLog(
  reservationId: string,
): Promise<{ items: ReservationAuditLogEntry[] }> {
  return apiRequest<{ items: ReservationAuditLogEntry[] }>(
    'GET',
    `/reservations/${reservationId}/audit-log`,
  );
}

// --- /timecards ------------------------------------------------------------

export type ListTimecardsParams = {
  from: string;
  to: string;
  locationId?: string;
  userId?: string;
};

export function listTimecards(params: ListTimecardsParams): Promise<{ items: TeamsTimecard[] }> {
  const qs = buildQs(params);
  return apiRequest<{ items: TeamsTimecard[] }>('GET', `/timecards${qs}`);
}

export function listShifts(params: ListTimecardsParams): Promise<{ items: TeamsShift[] }> {
  const qs = buildQs(params);
  return apiRequest<{ items: TeamsShift[] }>('GET', `/shifts${qs}`);
}

export function triggerTimecardSync(): Promise<TeamsSyncSummary> {
  return apiRequest<TeamsSyncSummary>('POST', '/timecards/sync');
}

// --- /tap-change-log -------------------------------------------------------

export function listTapChangeLog(params?: {
  locationId?: string;
  offset?: number;
  limit?: number;
}): Promise<ListEnvelope<TapChangeLogEntry>> {
  const qs = buildQs(params ?? {});
  return apiRequest<ListEnvelope<TapChangeLogEntry>>('GET', `/tap-change-log${qs}`);
}

// ---------------------------------------------------------------------------
// Query-string builder
// ---------------------------------------------------------------------------

function buildQs(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const parts: string[] = [];
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined || val === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}
